/**
 * /admin/prep-pod — visibility for every Prep Pod signup.
 * Shows RSVPs (Open House + Private Tour), full Applications, and
 * Charter Fund invoice requests. CSV export per section.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Loader2, RefreshCw, ArrowLeft, Download, Mail, AlertTriangle,
  CalendarCheck, ClipboardList, Landmark, Send,
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';
import EnvBanner from '../components/EnvBanner';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });

const fmtDT = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (isNaN(d)) return '—';
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch { return '—'; }
};

const toCSV = (rows, columns) => {
  const esc = (v) => {
    if (v === null || v === undefined) return '';
    const s = String(v).replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  };
  const header = columns.map((c) => esc(c.label)).join(',');
  const body = rows.map((r) => columns.map((c) => esc(c.get(r))).join(',')).join('\n');
  return `${header}\n${body}\n`;
};

const download = (filename, csv) => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const SectionCard = ({ icon: Icon, color, title, count, rows, columns, filename, emptyLabel, extra }) => (
  <div className="border-2 border-gray-800 bg-[#0a0a0a] p-5" style={{ borderColor: rows.length ? color : undefined }} data-testid={`prep-pod-section-${filename}`}>
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-800 pb-3 mb-4">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 flex items-center justify-center border-2" style={{ borderColor: color, color }}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <div className="text-[10px] font-black uppercase tracking-[0.28em]" style={{ color }}>{title}</div>
          <div className="text-2xl font-black text-white leading-none mt-1">{count}</div>
        </div>
      </div>
      {extra}
      <button
        onClick={() => download(filename, toCSV(rows, columns))}
        disabled={!rows.length}
        className="flex items-center gap-2 border-2 border-gray-800 hover:border-orange-500 disabled:opacity-30 disabled:cursor-not-allowed px-3 py-2 text-[10px] font-black uppercase tracking-[0.2em] text-gray-300 hover:text-white transition-colors"
        data-testid={`prep-pod-csv-${filename}`}
      >
        <Download className="w-3.5 h-3.5" /> CSV
      </button>
    </div>

    {rows.length === 0 ? (
      <div className="text-xs text-gray-600 py-6 text-center border-2 border-dashed border-gray-800">
        {emptyLabel}
      </div>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs" data-testid={`prep-pod-table-${filename}`}>
          <thead>
            <tr className="text-gray-500 uppercase tracking-widest text-[9px] border-b border-gray-800">
              {columns.map((c) => (
                <th key={c.label} className="py-2 pr-3 whitespace-nowrap">{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id || i} className="border-b border-gray-900 hover:bg-black/40" data-testid={`prep-pod-row-${filename}-${i}`}>
                {columns.map((c) => (
                  <td key={c.label} className="py-2 pr-3 align-top">
                    {c.render ? c.render(r) : (c.get(r) ?? '—')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const TIER_LABEL = {
  '3_4_days': '3–4 Days/Week ($30/session)',
  '1_2_days': '1–2 Days/Week ($40/session)',
  'drop_in': 'Drop-In ($45)',
};
const FUNDING_LABEL = {
  taylion: 'Taylion Charter',
  epic_california: 'Epic CA Charter',
  private_pay: 'Private Pay',
};
const COUNTING_LABEL = {
  up_to_10: 'Up to 10',
  up_to_20: 'Up to 20',
  up_to_50: 'Up to 50',
  '50_plus': '50+',
};
const LITERACY_LABEL = {
  letter_recognition: 'Letter Recognition',
  letter_sounds: 'Letter Sounds',
  blending: 'Blending',
  fluent: 'Fluent',
};

const PrepPodAdmin = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [testing, setTesting] = useState(false);
  const [authed, setAuthed] = useState(true);

  const load = async () => {
    setRefreshing(true);
    try {
      const r = await api.get('/prep-pod/admin/signups', { headers: authHeaders() });
      setData(r.data);
      setAuthed(true);
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        setAuthed(false);
      } else {
        toast.error('Could not load signups');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  useEffect(() => { load(); }, []);

  const sendTest = async () => {
    setTesting(true);
    try {
      const r = await api.post('/prep-pod/admin/test-email', {}, { headers: authHeaders() });
      if (r.data.sent) toast.success(`Test email sent to ${r.data.to}`);
      else toast.error(r.data.reason || 'Email send failed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test failed');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <ShieldMissing />
          <h1 className="text-2xl font-black uppercase tracking-tight mt-4">Admin access required</h1>
          <p className="text-xs text-gray-500 mt-2">Sign in from the Admin Hub, then come back.</p>
          <Link to="/admin" className="mt-6 inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-black px-5 py-3 text-[10px] font-black uppercase tracking-[0.25em]" data-testid="prep-pod-admin-signin-link">
            <ArrowLeft className="w-3.5 h-3.5" /> Go to Admin Hub
          </Link>
        </div>
      </div>
    );
  }

  const rsvps = data?.rsvps || [];
  const applications = data?.applications || [];
  const charter = data?.charter_requests || [];
  const total = data?.counts?.total ?? (rsvps.length + applications.length + charter.length);
  const resendOk = data?.resend_configured;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="prep-pod-admin">
      <EnvBanner />
      <div className="py-10 px-4">
        <div className="mx-auto w-full max-w-6xl">
          {/* Header */}
          <div className="flex items-start justify-between gap-4 border-b border-gray-800 pb-6">
            <div>
              <Link to="/admin" className="text-[10px] font-bold tracking-[0.28em] text-gray-500 hover:text-orange-400 uppercase flex items-center gap-1 mb-3" data-testid="prep-pod-admin-back">
                <ArrowLeft className="w-3 h-3" /> Admin Hub
              </Link>
              <div className="text-[10px] font-bold tracking-[0.4em] text-orange-500 uppercase">The Prep Pod</div>
              <h1 className="mt-2 text-3xl sm:text-4xl font-black uppercase tracking-tight">Signups <span className="text-orange-500">({total})</span></h1>
              <p className="text-xs text-gray-500 mt-2">Every RSVP, application and charter-fund request — pulled straight from the database.</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={sendTest}
                disabled={testing}
                className="flex items-center gap-2 border-2 border-gray-800 hover:border-blue-500 px-3 py-2 text-[10px] font-black uppercase tracking-[0.2em] text-gray-300 hover:text-white transition-colors"
                data-testid="prep-pod-admin-test-email"
                title="Send a test email to Margaret to verify the notification pipe"
              >
                {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Test Email
              </button>
              <button
                onClick={load}
                disabled={refreshing}
                className="flex items-center gap-2 border-2 border-gray-800 hover:border-orange-500 px-3 py-2 text-[10px] font-black uppercase tracking-[0.2em] text-gray-300 hover:text-white transition-colors"
                data-testid="prep-pod-admin-refresh"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
              </button>
            </div>
          </div>

          {/* Health banner */}
          <div className={`mt-6 border-2 p-4 flex items-start gap-3 ${resendOk ? 'border-emerald-500/40 bg-emerald-500/[0.04]' : 'border-red-500/50 bg-red-500/[0.06]'}`} data-testid="prep-pod-admin-health">
            {resendOk ? (
              <Mail className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
            )}
            <div className="text-xs leading-relaxed">
              {resendOk ? (
                <>
                  <div className="font-black uppercase tracking-widest text-emerald-400 text-[10px]">Resend configured</div>
                  <div className="text-gray-400 mt-1">Notifications go to <span className="font-mono text-white">{data?.notify_email}</span>. If Margaret still isn't seeing them, click <em>Test Email</em> above — if the test lands, real signups are landing too (check her spam / promotions folder).</div>
                </>
              ) : (
                <>
                  <div className="font-black uppercase tracking-widest text-red-400 text-[10px]">Notifications are OFF in this environment</div>
                  <div className="text-gray-400 mt-1"><span className="font-mono text-white">RESEND_API_KEY</span> is not set — every signup below saved to the database, but no email was ever sent. Add the key in production env and every future signup will notify {data?.notify_email}.</div>
                </>
              )}
            </div>
          </div>

          {/* RSVPs */}
          <div className="mt-8 space-y-8">
            <SectionCard
              icon={CalendarCheck}
              color="#FFC72C"
              title="Open House RSVPs & Private Tours"
              count={rsvps.length}
              rows={rsvps}
              filename="prep-pod-rsvps.csv"
              emptyLabel="No RSVPs yet. Share the /prep-pod link to start collecting bookings."
              columns={[
                { label: 'When', get: (r) => fmtDT(r.created_at) },
                { label: 'Type', get: (r) => r.booking_type === 'open_house' ? 'Open House' : 'Private Tour' },
                { label: 'Slot', get: (r) => r.slot_label || r.slot_id || '' },
                { label: 'Parent', get: (r) => r.parent_name || '' },
                {
                  label: 'Email',
                  get: (r) => r.parent_email || '',
                  render: (r) => r.parent_email ? <a href={`mailto:${r.parent_email}`} className="text-blue-400 hover:underline">{r.parent_email}</a> : '—',
                },
                {
                  label: 'Phone',
                  get: (r) => r.parent_phone || '',
                  render: (r) => r.parent_phone ? <a href={`tel:${r.parent_phone}`} className="text-blue-400 hover:underline">{r.parent_phone}</a> : '—',
                },
                { label: 'Child', get: (r) => r.child_name || '' },
                { label: 'Grade', get: (r) => r.child_grade || '' },
                { label: 'Notes', get: (r) => r.notes || '' },
              ]}
            />

            {/* Applications */}
            <SectionCard
              icon={ClipboardList}
              color="#38BDF8"
              title="Full Applications"
              count={applications.length}
              rows={applications}
              filename="prep-pod-applications.csv"
              emptyLabel="No applications yet."
              columns={[
                { label: 'When', get: (r) => fmtDT(r.created_at) },
                { label: 'Parent', get: (r) => r.parent_name || '' },
                {
                  label: 'Contact',
                  get: (r) => `${r.parent_email || ''} · ${r.parent_phone || ''}`,
                  render: (r) => (
                    <div className="space-y-0.5">
                      {r.parent_email && <div><a href={`mailto:${r.parent_email}`} className="text-blue-400 hover:underline">{r.parent_email}</a></div>}
                      {r.parent_phone && <div><a href={`tel:${r.parent_phone}`} className="text-blue-400 hover:underline">{r.parent_phone}</a></div>}
                    </div>
                  ),
                },
                {
                  label: 'Child',
                  get: (r) => `${r.child_name || ''}${r.child_age ? ` (age ${r.child_age})` : ''}`,
                },
                { label: 'Target Grade', get: (r) => r.target_grade || '' },
                { label: 'Counting', get: (r) => COUNTING_LABEL[r.counting_level] || r.counting_level || '' },
                { label: 'Literacy', get: (r) => LITERACY_LABEL[r.literacy_level] || r.literacy_level || '' },
                { label: 'Funding', get: (r) => FUNDING_LABEL[r.funding] || r.funding || '' },
                { label: 'Tier', get: (r) => TIER_LABEL[r.pricing_tier] || r.pricing_tier || '' },
                { label: 'Cycle', get: (r) => r.pricing_cycle || '' },
                { label: 'Notes', get: (r) => r.notes || '' },
              ]}
            />

            {/* Charter fund invoice requests */}
            <SectionCard
              icon={Landmark}
              color="#A78BFA"
              title="Charter Fund Invoice Requests"
              count={charter.length}
              rows={charter}
              filename="prep-pod-charter-requests.csv"
              emptyLabel="No charter invoice requests yet."
              columns={[
                { label: 'When', get: (r) => fmtDT(r.created_at) },
                { label: 'Charter', get: (r) => FUNDING_LABEL[r.charter] || r.charter || '' },
                { label: 'Parent', get: (r) => r.parent_name || '' },
                {
                  label: 'Email',
                  get: (r) => r.parent_email || '',
                  render: (r) => r.parent_email ? <a href={`mailto:${r.parent_email}`} className="text-blue-400 hover:underline">{r.parent_email}</a> : '—',
                },
                {
                  label: 'Phone',
                  get: (r) => r.parent_phone || '',
                  render: (r) => r.parent_phone ? <a href={`tel:${r.parent_phone}`} className="text-blue-400 hover:underline">{r.parent_phone}</a> : '—',
                },
                { label: 'Child', get: (r) => r.child_name || '' },
                { label: 'Grade', get: (r) => r.target_grade || '' },
                { label: 'Tier', get: (r) => TIER_LABEL[r.pricing_tier] || r.pricing_tier || '' },
                { label: 'Notes', get: (r) => r.notes || '' },
              ]}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const ShieldMissing = () => (
  <div className="mx-auto w-12 h-12 flex items-center justify-center border-2 border-red-500 text-red-500">
    <AlertTriangle className="w-6 h-6" />
  </div>
);

export default PrepPodAdmin;
